import React, { useState, useEffect } from 'react';
import { FaTimes, FaChartBar, FaChartLine, FaChartPie, FaChartArea, FaFilter } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Scatter } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AreaChart } from 'lucide-react';
import jwt_decode from 'jwt-decode';
import {
  buildPeriodOverPeriodRecipe,
  growthMetricId,
} from '../../../utils/derivedMetrics';
import ChartRenderer from './ChartRenderer';
import { resolveSummarizedFieldMeta } from './adapters/extractChartSeries';
import BarChart from './BarChart';
import ScatterChart from './ScatterChart';
import DonutChart from './DonutChart';
import {
  getCategoryAxisColorsForChart,
  appendAlphaIfHex,
  buildPicklistValueColorMap,
  isPicklistGroupField,
} from './chartPicklistColors';
import {
  getMatrixChartDefaults,
  getSummaryChartDefaults,
  MATRIX_CHART_TOTAL_COLOR_KEY,
  MATRIX_CHART_TOTAL_DEFAULT_COLOR,
  isChartGrandTotalCategory,
} from '../matrix/matrixChartData';
import { collectSummarizedMeasureFields } from '../matrix/matrixReportConfig';
import './ReportChart.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ReportChart = ({
  isOpen,
  onClose,
  reportData,
  onSaveChart,
  chartValues = [],
  chartLabels = [],
  xAxisLabel = '',
  yAxisLabel = '',
  chartTitle = '',
  summarizedData = {}, // Enhanced summarized data with field details
  summarizedFields = {}, // Field definitions for summarized data
  previewData = [], // Preview data for record count calculation
  selectedGroups = [], // Row groups (matrix + summary)
  selectedColumnGroups = [], // Column groups (matrix)
  selectedFields = [],
  matrixDateGranularity = 'month',
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  multiCurrencyEnabled = true,
  editingChart = null // Chart being edited
}) => {
  const reportConversionBaseCode = multiCurrencyEnabled ? baseCurrencyCode : null;
  const reportConversionLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  const isMatrixReport =
    summarizedData?._metadata?.report_info?.report_type === 'matrix' ||
    (Array.isArray(selectedColumnGroups) && selectedColumnGroups.length > 0);
  const hasMultipleRowGroups =
    (Array.isArray(selectedGroups) && selectedGroups.length >= 2) ||
    (summarizedData?._metadata?.report_info?.group_count ?? 0) >= 2;
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsSaving(false);
    }
  }, [isOpen]);

  const [chartConfig, setChartConfig] = useState({
    title: '',
    chartType: 'bar',
    selectedCategoryField: '', // Field for X-axis (categories)
    selectedValueFields: [],   // Multiple fields for Y-axis (values) - changed to array
    xAxisLabel: '',            // Custom X-axis label
    yAxisLabel: '',            // Custom Y-axis label
    fieldColors: {},           // Custom colors for each field
    showTooltips: true,        // Show tooltips on hover
    showLabels: false,         // Show data labels on chart elements
    labelColor: '#000000',     // Color for data labels
    scatterXAxisLabel: '',     // Custom X-axis label for scatter charts
    scatterYAxisLabel: '',     // Custom Y-axis label for scatter charts
    sortOrder: 1,              // Sort order for chart display
    columnSpan: 1,             // Column span: 1 = half width, 2 = full width
    // Donut chart specific properties
    pieValueField: '',         // Value field for donut chart (summarized field)
    pieSliceField: '',         // Field to slice by (grouped field)
    showValues: false,         // Show values on donut chart segments
    showPercentages: false,    // Show percentages on donut chart segments
    showTotal: true,           // Show total in center of doughnut chart
    legendColors: {},          // Custom colors for legend items {label: color}
    legendPosition: 'bottom',  // Legend position
    seriesTypes: {},           // mixed chart: { fieldName: 'bar'|'line' }
    funnelStageField: '',
    funnelValueField: '',
    stackByField: '', // Matrix: dimension for stack segments (e.g. Stage)
    matrixMeasureField: '', // Matrix: Y-axis measure (e.g. Sum of Amount)
    matrixLineField: '', // Matrix: optional line overlay (e.g. Win Rate %)
    showMatrixColumnTotal: false, // Matrix: show Total as a chart column (tooltip total always on)
    chartFullHeight: false, // Use 100% height of chart container when enabled
    stackTo100Percent: false, // Stacked/matrix: normalize segments to 100% per category
    showPeriodGrowth: false,
    derivedMetrics: [],
  });

  const [isLoading, setIsLoading] = useState(false);

  // Chart types available
  const chartTypes = [
    { value: 'bar', label: 'Column Chart', icon: FaChartBar },
    { value: 'stackedColumn', label: 'Stacked Column', icon: FaChartBar },
    { value: 'horizontalBar', label: 'Bar Chart', icon: FaChartBar },
    { value: 'stackedBar', label: 'Stacked Bar', icon: FaChartBar },
    { value: 'line', label: 'Line Chart', icon: FaChartLine },
    { value: 'mixed', label: 'Mixed (Bar + Line)', icon: FaChartLine },
    { value: 'area', label: 'Area Chart', icon: FaChartArea },
    { value: 'donut', label: 'Donut Chart', icon: FaChartPie },
    { value: 'funnel', label: 'Funnel Chart', icon: FaFilter },
    { value: 'scatter', label: 'Scatter Chart', icon: FaChartLine },
  ];

  const previewEngine =
    editingChart?.chartEngine ||
    (chartConfig.chartType === 'scatter' ? 'chartjs' : 'echarts');

  const isStackedChartType =
    chartConfig.chartType === 'stackedColumn' ||
    chartConfig.chartType === 'stackedBar';
  const showStackByChartConfig =
    isMatrixReport || (hasMultipleRowGroups && isStackedChartType);

  const handlePeriodGrowthToggle = (enabled) => {
    const sourceField = isMatrixReport
      ? chartConfig.matrixMeasureField || chartConfig.selectedValueFields?.[0]
      : chartConfig.selectedValueFields?.[0];
    const groupField = chartConfig.selectedCategoryField;

    if (enabled && (!sourceField || !groupField)) {
      alert('Select a category (X-axis) field and at least one value field before adding growth %.');
      return;
    }

    setChartConfig((prev) => {
      if (!enabled) {
        const growthIds = (prev.derivedMetrics || []).map((r) => r.id).filter(Boolean);
        return {
          ...prev,
          showPeriodGrowth: false,
          derivedMetrics: [],
          selectedValueFields: (prev.selectedValueFields || []).filter(
            (f) => !growthIds.includes(f)
          ),
        };
      }

      const recipe = buildPeriodOverPeriodRecipe({ sourceField, groupField });
      if (!recipe) return prev;

      const growthId = recipe.id;
      const valueFields = [...(prev.selectedValueFields || [])];
      if (!valueFields.includes(growthId)) {
        valueFields.push(growthId);
      }

      return {
        ...prev,
        showPeriodGrowth: true,
        derivedMetrics: [recipe],
        selectedValueFields: valueFields,
      };
    });
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setChartConfig(prev => {
      const newConfig = { ...prev, [field]: value };

      // Reset chart-specific fields when chart type changes
      if (field === 'chartType') {
        if (value === 'donut' || value === 'funnel') {
          // Reset to donut/funnel chart defaults
          newConfig.pieValueField = '';
          newConfig.pieSliceField = '';
          newConfig.funnelStageField = '';
          newConfig.funnelValueField = '';
          newConfig.showValues = false;
          newConfig.showPercentages = false;
          newConfig.showTotal = true;
          newConfig.legendColors = {};
          newConfig.valueFieldType = 'number';
          // Clear regular chart fields
          newConfig.selectedCategoryField = '';
          newConfig.selectedValueFields = [];
          newConfig.xAxisLabel = '';
          newConfig.yAxisLabel = '';
          newConfig.fieldColors = {};
        } else if (value === 'mixed') {
          const fields = newConfig.selectedValueFields || [];
          newConfig.seriesTypes =
            fields.length >= 2
              ? { [fields[0]]: 'bar', [fields[1]]: 'line' }
              : {};
        } else if (value === 'scatter') {
          // Reset to scatter chart defaults
          newConfig.scatterXAxis = '';
          newConfig.scatterYAxis = '';
          newConfig.scatterGroupField = '';
          newConfig.scatterXAxisLabel = '';
          newConfig.scatterYAxisLabel = '';
          // Clear donut chart fields
          newConfig.pieValueField = '';
          newConfig.pieSliceField = '';
          newConfig.showValues = false;
          newConfig.showPercentages = false;
          newConfig.showTotal = true;
          newConfig.legendColors = {};
        } else {
          // Reset to regular chart defaults
          newConfig.selectedCategoryField = '';
          newConfig.selectedValueFields = [];
          newConfig.xAxisLabel = '';
          newConfig.yAxisLabel = '';
          newConfig.fieldColors = {};
          // Clear donut chart fields
          newConfig.pieValueField = '';
          newConfig.pieSliceField = '';
          newConfig.showValues = false;
          newConfig.showPercentages = false;
          newConfig.showTotal = true;
          newConfig.legendColors = {};
          // Clear scatter chart fields
          newConfig.scatterXAxis = '';
          newConfig.scatterYAxis = '';
          newConfig.scatterGroupField = '';
          newConfig.scatterXAxisLabel = '';
          newConfig.scatterYAxisLabel = '';
        }
      }

      return newConfig;
    });
  };

  // Convert rgba to hex for color picker
  const rgbaToHex = (rgba) => {
    const values = rgba.match(/\d+/g);
    if (!values || values.length < 3) return '#000000';

    const r = parseInt(values[0]);
    const g = parseInt(values[1]);
    const b = parseInt(values[2]);

    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Get default color for a label based on its index
  const getDefaultColorForLabel = (labelIndex) => {
    const baseColors = [
      'rgba(54, 162, 235, 0.8)',   // Blue
      'rgba(255, 99, 132, 0.8)',   // Red
      'rgba(255, 205, 86, 0.8)',   // Yellow
      'rgba(75, 192, 192, 0.8)',   // Teal
      'rgba(153, 102, 255, 0.8)',  // Purple
      'rgba(255, 159, 64, 0.8)',   // Orange
      'rgba(199, 199, 199, 0.8)',  // Grey
      'rgba(83, 102, 255, 0.8)',   // Indigo
      'rgba(255, 99, 255, 0.8)',   // Pink
      'rgba(99, 255, 132, 0.8)',   // Green
    ];
    return baseColors[labelIndex % baseColors.length];
  };

  // Get default hex color for color picker
  const getDefaultHexColorForLabel = (labelIndex) => {
    const rgbaColor = getDefaultColorForLabel(labelIndex);
    return rgbaToHex(rgbaColor);
  };

  // Handle legend color change for donut charts
  const handleLegendColorChange = (label, color) => {
    setChartConfig(prev => {
      const newLegendColors = {
        ...prev.legendColors,
        [label]: color
      };
      return {
        ...prev,
        legendColors: newLegendColors
      };
    });

    // Force chart re-render by updating a timestamp
    setTimeout(() => {
      setChartConfig(prev => ({ ...prev, _colorUpdate: Date.now() }));
    }, 100);
  };

  // Reset legend color to default
  const resetLegendColorToDefault = (label) => {
    setChartConfig(prev => {
      const newLegendColors = { ...prev.legendColors };
      delete newLegendColors[label]; // Remove custom color to use default
      return {
        ...prev,
        legendColors: newLegendColors
      };
    });

    // Force chart re-render
    setTimeout(() => {
      setChartConfig(prev => ({ ...prev, _colorUpdate: Date.now() }));
    }, 100);
  };

  // Handle multiple value field selection
  const handleValueFieldToggle = (fieldName) => {
    setChartConfig(prev => {
      const currentFields = prev.selectedValueFields || [];
      const isSelected = currentFields.includes(fieldName);

      let newFields;
      let newFieldColors = { ...prev.fieldColors };

      const newSeriesTypes = { ...(prev.seriesTypes || {}) };

      if (isSelected) {
        // Remove field if already selected
        newFields = currentFields.filter(f => f !== fieldName);
        // Remove the color for this field
        delete newFieldColors[fieldName];
        delete newSeriesTypes[fieldName];
      } else {
        // Add field if not selected
        newFields = [...currentFields, fieldName];
        // Set default color for this field if not already set
        if (!newFieldColors[fieldName]) {
          const availableFields = getAvailableValueFields();
          const fieldIndex = availableFields.findIndex(f => f.value === fieldName);
          newFieldColors[fieldName] = getColorForField(fieldName, fieldIndex);
        }
        if (!newSeriesTypes[fieldName]) {
          const hasBar = Object.values(newSeriesTypes).some((t) => t === 'bar');
          newSeriesTypes[fieldName] =
            prev.chartType === 'mixed' && hasBar ? 'line' : 'bar';
        }
      }

      return {
        ...prev,
        selectedValueFields: newFields,
        fieldColors: newFieldColors,
        seriesTypes: newSeriesTypes,
      };
    });
  };

  // Generate auto labels based on selected fields
  const generateAutoLabels = () => {
    let autoXAxisLabel = 'Categories';
    let autoYAxisLabel = 'Values';

    if (summarizedData && summarizedData._metadata) {
      const metadata = summarizedData._metadata;

      // Generate X-axis label
      if (chartConfig.selectedCategoryField && metadata.group_fields[chartConfig.selectedCategoryField]) {
        const groupField = metadata.group_fields[chartConfig.selectedCategoryField];
        autoXAxisLabel = groupField.field_label || chartConfig.selectedCategoryField;
      }

      // Stack / matrix measure takes priority for Y-axis label
      const measureField =
        chartConfig.matrixMeasureField ||
        (chartConfig.selectedValueFields?.length === 1
          ? chartConfig.selectedValueFields[0]
          : null);

      if (measureField === 'record_count') {
        autoYAxisLabel = 'Record Count';
      } else if (measureField && metadata.summarized_fields?.[measureField]) {
        const summarizedField = metadata.summarized_fields[measureField];
        const aggregation = summarizedField.aggregation?.toUpperCase() || 'SUM';
        autoYAxisLabel = `${summarizedField.field_label || measureField} (${aggregation})`;
      } else if (chartConfig.selectedValueFields && chartConfig.selectedValueFields.length > 1) {
        autoYAxisLabel = `${chartConfig.selectedValueFields.length} Metrics`;
      } else if (chartConfig.selectedValueFields?.length === 1) {
        const fieldName = chartConfig.selectedValueFields[0];
        if (metadata.summarized_fields?.[fieldName]) {
          const summarizedField = metadata.summarized_fields[fieldName];
          const aggregation = summarizedField.aggregation?.toUpperCase() || 'SUM';
          autoYAxisLabel = `${summarizedField.field_label || fieldName} (${aggregation})`;
        }
      }
    }

    return { autoXAxisLabel, autoYAxisLabel };
  };

  // Generate auto labels for scatter charts based on selected fields
  const generateScatterAutoLabels = () => {
    let autoScatterXAxisLabel = '';
    let autoScatterYAxisLabel = '';

    if (summarizedData && summarizedData._metadata) {
      const metadata = summarizedData._metadata;

      // Generate X-axis label for scatter chart
      if (chartConfig.scatterXAxis && metadata.summarized_fields[chartConfig.scatterXAxis]) {
        const xField = metadata.summarized_fields[chartConfig.scatterXAxis];
        const aggregation = xField.aggregation?.toUpperCase() || 'SUM';
        autoScatterXAxisLabel = `${xField.field_label || chartConfig.scatterXAxis} (${aggregation})`;
      }

      // Generate Y-axis label for scatter chart
      if (chartConfig.scatterYAxis && metadata.summarized_fields[chartConfig.scatterYAxis]) {
        const yField = metadata.summarized_fields[chartConfig.scatterYAxis];
        const aggregation = yField.aggregation?.toUpperCase() || 'SUM';
        autoScatterYAxisLabel = `${yField.field_label || chartConfig.scatterYAxis} (${aggregation})`;
      }
    }

    return { autoScatterXAxisLabel, autoScatterYAxisLabel };
  };

  // Generate colors for different fields
  const getColorForField = (fieldName, index) => {
    const colors = [
      '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#C9CBCF', '#8B5CF6', '#10B981', '#F59E0B',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#8B5A2B'
    ];
    return colors[index % colors.length];
  };

  // Helper function to calculate record count for a specific group
  const calculateRecordCountForGroup = (groupValue, groupField) => {
    if (!previewData || previewData.length === 0 || !groupField) {
      return 0;
    }

    // Filter records that belong to this group
    const recordsInGroup = previewData.filter(row => {
      const tableName = groupField.object_name || 'main';
      const aliasedFieldName = `${tableName}_${groupField.field_name}`;
      const fieldData = row[aliasedFieldName] || row[groupField.field_name];

      let rowGroupValue = 'N/A';
      if (fieldData) {
        if (typeof fieldData === 'object' && fieldData !== null) {
          rowGroupValue = fieldData.displayValue || fieldData.value || 'N/A';
        } else {
          rowGroupValue = fieldData;
        }
      }
      return rowGroupValue === groupValue;
    });

    return recordsInGroup.length;
  };

  // Handle color change for a specific field
  const handleFieldColorChange = (fieldName, color) => {
    setChartConfig(prev => ({
      ...prev,
      fieldColors: {
        ...prev.fieldColors,
        [fieldName]: color
      }
    }));
  };

  // Get color for a specific field (custom or default)
  const getFieldColor = (fieldName, index) => {
    if (chartConfig.fieldColors && chartConfig.fieldColors[fieldName]) {
      return chartConfig.fieldColors[fieldName];
    }
    return getColorForField(fieldName, index);
  };

  const getMatrixTotalColor = () =>
    chartConfig.fieldColors?.[MATRIX_CHART_TOTAL_COLOR_KEY] ||
    chartConfig.legendColors?.[MATRIX_CHART_TOTAL_COLOR_KEY] ||
    MATRIX_CHART_TOTAL_DEFAULT_COLOR;

  // Pre-fill form when editing an existing chart
  useEffect(() => {
    if (editingChart && isOpen) {
      console.log('Pre-filling form with editing chart:', editingChart);
      // Ensure default colors are set for any fields without custom colors (only for non-scatter charts)
      const chartType = editingChart.chartType || 'bar';
      let selectedFields = [];
      let existingColors = {};
      let defaultColors = {};

      if (chartType !== 'scatter') {
        selectedFields = editingChart.yAxisFields || (editingChart.yAxisField ? [editingChart.yAxisField] : []);
        existingColors = editingChart.fieldColors || {};

        selectedFields.forEach(fieldName => {
          if (!existingColors[fieldName]) {
            const availableFields = getAvailableValueFields();
            const fieldIndex = availableFields.findIndex(f => f.value === fieldName);
            defaultColors[fieldName] = getColorForField(fieldName, fieldIndex);
          }
        });
      }

      // Set chart configuration based on chart type

      setChartConfig({
        title: editingChart.title || '',
        chartType: chartType,
        ...(chartType === 'scatter' ? {
          // Scatter chart configuration
          selectedCategoryField: '',
          selectedValueFields: [],
          xAxisLabel: '',
          yAxisLabel: '',
          scatterXAxis: editingChart.scatterXAxis || '',
          scatterYAxis: editingChart.scatterYAxis || '',
          scatterGroupField: editingChart.scatterGroupField || '',
          scatterXAxisLabel: editingChart.scatterXAxisLabel || '',
          scatterYAxisLabel: editingChart.scatterYAxisLabel || '',
        } : {
          // Regular chart configuration
          selectedCategoryField: editingChart.xAxisField || '',
          selectedValueFields: selectedFields,
          xAxisLabel: editingChart.xAxisLabel || '',
          yAxisLabel: editingChart.yAxisLabel || '',
          scatterXAxis: '',
          scatterYAxis: '',
          scatterGroupField: '',
          scatterXAxisLabel: '',
          scatterYAxisLabel: '',
        }),
        fieldColors: { ...existingColors, ...defaultColors },
        showTooltips: editingChart.showTooltips !== undefined ? editingChart.showTooltips : true,
        showLabels: editingChart.showLabels !== undefined ? editingChart.showLabels : false,
        labelColor: editingChart.labelColor || '#000000',
        sortOrder: editingChart.sortOrder || 1,
        columnSpan: editingChart.columnSpan || 1,
        // Donut chart specific properties - map from standard fields if not already set
        pieValueField: editingChart.pieValueField || (editingChart.yAxisFields && editingChart.yAxisFields[0]) || '',
        pieSliceField: editingChart.pieSliceField || editingChart.xAxisField || '',
        showValues: editingChart.showValues !== undefined ? editingChart.showValues : false,
        showPercentages: editingChart.showPercentages !== undefined ? editingChart.showPercentages : false,
        showTotal: editingChart.showTotal !== undefined ? editingChart.showTotal : true,
        legendColors: editingChart.legendColors || {},
        legendPosition: editingChart.legendPosition || 'bottom',
        seriesTypes: editingChart.seriesTypes || {},
        funnelStageField:
          editingChart.funnelStageField ||
          editingChart.pieSliceField ||
          editingChart.xAxisField ||
          '',
        funnelValueField:
          editingChart.funnelValueField ||
          editingChart.pieValueField ||
          (editingChart.yAxisFields && editingChart.yAxisFields[0]) ||
          '',
        stackByField: editingChart.stackByField || '',
        matrixMeasureField:
          editingChart.matrixMeasureField ||
          (() => {
            const y =
              editingChart.yAxisField ||
              (editingChart.yAxisFields && editingChart.yAxisFields[0]) ||
              '';
            if (!isMatrixReport) return y;
            const line =
              editingChart.matrixLineField || editingChart.matrix_line_field;
            if (line && y === line) return 'record_count';
            const meta =
              summarizedData?._metadata?.summarized_fields?.[y];
            if (meta?.is_derived) return 'record_count';
            return y;
          })(),
        matrixLineField:
          editingChart.matrixLineField ||
          editingChart.matrix_line_field ||
          (() => {
            const y =
              editingChart.yAxisField ||
              (editingChart.yAxisFields && editingChart.yAxisFields[0]) ||
              '';
            const meta =
              summarizedData?._metadata?.summarized_fields?.[y];
            if (meta?.is_derived) return y;
            return '';
          })(),
        showMatrixColumnTotal:
          editingChart.showMatrixColumnTotal ??
          editingChart.show_matrix_column_total ??
          false,
        chartFullHeight:
          editingChart.chartFullHeight ?? editingChart.chart_full_height ?? false,
        stackTo100Percent:
          editingChart.stackTo100Percent ??
          editingChart.stack_to_100_percent ??
          false,
        derivedMetrics: editingChart.derivedMetrics || [],
        showPeriodGrowth:
          Array.isArray(editingChart.derivedMetrics) && editingChart.derivedMetrics.length > 0,
      });
    } else if (isOpen && !editingChart) {
      const matrixDefaultsResolved = isMatrixReport
        ? getMatrixChartDefaults(
            selectedGroups,
            selectedColumnGroups,
            selectedFields,
            summarizedFields
          )
        : null;
      const summaryDefaultsResolved =
        !isMatrixReport && hasMultipleRowGroups
          ? getSummaryChartDefaults(
              selectedGroups,
              selectedFields,
              summarizedFields
            )
          : null;
      const chartDefaultsResolved = matrixDefaultsResolved || summaryDefaultsResolved;
      // Reset form (matrix: X + column stack; summary: X + second row group stack)
      setChartConfig({
        title: '',
        chartType: chartDefaultsResolved?.chartType || 'bar',
        selectedCategoryField: chartDefaultsResolved?.selectedCategoryField || '',
        selectedValueFields: chartDefaultsResolved?.selectedValueFields || [],
        stackByField: chartDefaultsResolved?.stackByField || '',
        matrixMeasureField:
          chartDefaultsResolved?.matrixMeasureField ||
          chartDefaultsResolved?.selectedValueFields?.[0] ||
          'record_count',
        matrixLineField: chartDefaultsResolved?.matrixLineField || '',
        xAxisLabel: chartDefaultsResolved?.xAxisLabel || '',
        yAxisLabel: chartDefaultsResolved?.yAxisLabel || '',
        fieldColors: {},
        showTooltips: true,
        showLabels: false,
        labelColor: '#000000',
        scatterXAxis: '',
        scatterYAxis: '',
        scatterGroupField: '',
        scatterXAxisLabel: '',
        scatterYAxisLabel: '',
        legendPosition: 'bottom',
        sortOrder: 1,
        columnSpan: 1,
        pieValueField: '',
        pieSliceField: '',
        showValues: false,
        showPercentages: false,
        showTotal: true,
        legendColors: {},
        seriesTypes: {},
        funnelStageField: '',
        funnelValueField: '',
        showMatrixColumnTotal: false,
        chartFullHeight: false,
        stackTo100Percent: false,
        showPeriodGrowth: false,
        derivedMetrics: [],
      });
    }
  }, [editingChart, isOpen, summarizedData]);

  useEffect(() => {
    if (isMatrixReport || !hasMultipleRowGroups || !isOpen || editingChart) return;
    if (!isStackedChartType) return;
    const defaults = getSummaryChartDefaults(
      selectedGroups,
      selectedFields,
      summarizedFields
    );
    if (!defaults) return;
    setChartConfig((prev) => ({
      ...prev,
      selectedCategoryField:
        prev.selectedCategoryField || defaults.selectedCategoryField,
      stackByField: prev.stackByField || defaults.stackByField,
      matrixMeasureField:
        prev.matrixMeasureField || defaults.matrixMeasureField,
      selectedValueFields:
        prev.selectedValueFields?.length > 0
          ? prev.selectedValueFields
          : defaults.selectedValueFields,
      xAxisLabel: prev.xAxisLabel || defaults.xAxisLabel,
      yAxisLabel: prev.yAxisLabel || defaults.yAxisLabel,
    }));
  }, [
    chartConfig.chartType,
    hasMultipleRowGroups,
    isMatrixReport,
    isOpen,
    editingChart,
    selectedGroups,
    selectedFields,
    summarizedFields,
    isStackedChartType,
  ]);

  const buildPreviewChartObject = () => {
    const measureField =
      chartConfig.matrixMeasureField ||
      chartConfig.selectedValueFields?.[0];
    const yFields =
      showStackByChartConfig && measureField
        ? [measureField]
        : chartConfig.selectedValueFields;

    return {
    title: chartConfig.title,
    chartType: chartConfig.chartType,
    chartEngine: previewEngine,
    matrixChart: isMatrixReport,
    showMatrixColumnTotal: isMatrixReport
      ? !!chartConfig.showMatrixColumnTotal
      : false,
    chartFullHeight: !!chartConfig.chartFullHeight,
    stackTo100Percent: !!chartConfig.stackTo100Percent,
    xAxisField: chartConfig.selectedCategoryField,
    stackByField: chartConfig.stackByField,
    matrixLineField: chartConfig.matrixLineField || '',
    yAxisField: measureField,
    yAxisFields: yFields,
    xAxisLabel: chartConfig.xAxisLabel,
    yAxisLabel: chartConfig.yAxisLabel,
    fieldColors: chartConfig.fieldColors,
    legendColors: chartConfig.legendColors,
    seriesTypes: chartConfig.seriesTypes,
    pieValueField: chartConfig.pieValueField,
    pieSliceField: chartConfig.pieSliceField,
    funnelStageField:
      chartConfig.funnelStageField || chartConfig.selectedCategoryField,
    funnelValueField:
      chartConfig.funnelValueField ||
      chartConfig.selectedValueFields?.[0] ||
      chartConfig.pieValueField,
    scatterXAxis: chartConfig.scatterXAxis,
    scatterYAxis: chartConfig.scatterYAxis,
    scatterGroupField: chartConfig.scatterGroupField,
    scatterXAxisLabel: chartConfig.scatterXAxisLabel,
    scatterYAxisLabel: chartConfig.scatterYAxisLabel,
    showTooltips: chartConfig.showTooltips,
    showLabels: chartConfig.showLabels,
    showValues: chartConfig.showValues,
    showPercentages: chartConfig.showPercentages,
    showTotal: chartConfig.showTotal,
    legendPosition: chartConfig.legendPosition,
    labelColor: chartConfig.labelColor,
    valueFieldType: chartConfig.valueFieldType,
  };
  };

  // Update axis labels when field selections change
  useEffect(() => {
    if (isOpen && !editingChart) {
      // Only auto-update labels for new charts, not when editing
      const { autoXAxisLabel, autoYAxisLabel } = generateAutoLabels();
      const { autoScatterXAxisLabel, autoScatterYAxisLabel } = generateScatterAutoLabels();
      setChartConfig(prev => ({
        ...prev,
        xAxisLabel: prev.xAxisLabel || autoXAxisLabel,
        yAxisLabel: prev.yAxisLabel || autoYAxisLabel,
        scatterXAxisLabel: prev.scatterXAxisLabel || autoScatterXAxisLabel,
        scatterYAxisLabel: prev.scatterYAxisLabel || autoScatterYAxisLabel,
      }));
    }
  }, [
    chartConfig.selectedCategoryField,
    chartConfig.selectedValueFields,
    chartConfig.matrixMeasureField,
    chartConfig.scatterXAxis,
    chartConfig.scatterYAxis,
    isOpen,
    editingChart,
  ]);

  // Ensure default colors are set for newly selected fields
  useEffect(() => {
    if (chartConfig.selectedValueFields && chartConfig.selectedValueFields.length > 0) {
      const availableFields = getAvailableValueFields();
      const updatedColors = { ...chartConfig.fieldColors };
      let colorsUpdated = false;

      chartConfig.selectedValueFields.forEach(fieldName => {
        if (!updatedColors[fieldName]) {
          const fieldIndex = availableFields.findIndex(f => f.value === fieldName);
          updatedColors[fieldName] = getColorForField(fieldName, fieldIndex);
          colorsUpdated = true;
        }
      });

      if (colorsUpdated) {
        setChartConfig(prev => ({
          ...prev,
          fieldColors: updatedColors
        }));
      }
    }
  }, [chartConfig.selectedValueFields]);

  // Get available category fields (group fields)
  const getAvailableCategoryFields = () => {
    if (!summarizedData || !summarizedData._metadata) {
      return [];
    }

    const metadata = summarizedData._metadata;
    const categoryFields = [];

    if (metadata.group_fields) {
      Object.keys(metadata.group_fields).forEach(fieldName => {
        const fieldInfo = metadata.group_fields[fieldName];
        categoryFields.push({
          value: fieldName,
          label: fieldInfo.field_label || fieldInfo.field_name,
          fieldInfo: fieldInfo
        });
      });
    }

    return categoryFields;
  };

  // Get available value fields (summarized fields)
  const getAvailableValueFields = () => {
    if (!summarizedData || !summarizedData._metadata) {
      return [];
    }

    const metadata = summarizedData._metadata;
    const valueFields = [];
    const seen = new Set();

    const appendMeasure = (field) => {
      if (!field?.field_name || seen.has(field.field_name)) return;
      seen.add(field.field_name);
      const label = `${field.field_label || field.field_name} (${(field.aggregation || 'sum').toUpperCase()})`;
      valueFields.push({
        value: field.field_name,
        label,
        fieldInfo: {
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          aggregation: field.aggregation,
          calculatedReturnType: field.calculatedReturnType,
        },
      });
    };

    const groupedFields = Object.keys(metadata.group_fields || {}).map(
      (name) => metadata.group_fields[name]
    );
    collectSummarizedMeasureFields(
      selectedFields,
      summarizedFields,
      groupedFields
    ).forEach(appendMeasure);

    if (metadata.summarized_fields) {
      Object.keys(metadata.summarized_fields).forEach((fieldName) => {
        if (fieldName === 'record_count') return;
        const fieldInfo = metadata.summarized_fields[fieldName];
        if (fieldInfo?.is_derived) {
          if (seen.has(fieldName)) return;
          seen.add(fieldName);
          valueFields.push({
            value: fieldName,
            label: fieldInfo.field_label || fieldInfo.field_name,
            fieldInfo,
          });
          return;
        }
        if (seen.has(fieldName)) return;
        seen.add(fieldName);
        const label = `${fieldInfo.field_label || fieldInfo.field_name} (${fieldInfo.aggregation?.toUpperCase() || 'SUM'})`;
        valueFields.push({
          value: fieldName,
          label,
          fieldInfo,
        });
      });
    }

    if (
      metadata.group_fields &&
      Object.keys(metadata.group_fields).length > 0 &&
      (metadata.report_info?.report_type !== 'matrix' || !isMatrixReport)
    ) {
      valueFields.push({
        value: 'record_count',
        label: 'Record Count (COUNT)',
        fieldInfo: {
          field_label: 'Record Count',
          field_name: 'record_count',
          aggregation: 'count',
          data_type: 'number',
          is_virtual: true
        }
      });
    }

    return valueFields;
  };

  const getAvailableMatrixMeasures = () => {
    const measures = getAvailableValueFields().filter(
      (f) => !f.fieldInfo?.is_derived
    );
    if (!isMatrixReport) return measures;
    const hasRecordCount = measures.some((f) => f.value === 'record_count');
    if (hasRecordCount) return measures;
    return [
      ...measures,
      {
        value: 'record_count',
        label: 'Record Count (COUNT)',
        fieldInfo: {
          field_label: 'Record Count',
          field_name: 'record_count',
          aggregation: 'count',
        },
      },
    ];
  };

  const getAvailableMatrixLineFields = () =>
    getAvailableValueFields().filter(
      (f) =>
        f.fieldInfo?.is_derived ||
        String(f.fieldInfo?.data_type || '').toLowerCase() === 'percent'
    );

  const getAvailableStackByFields = () =>
    getAvailableCategoryFields().filter(
      (f) => f.value !== chartConfig.selectedCategoryField
    );

  const getStackByFieldInfo = () => {
    const stackField = chartConfig.stackByField;
    if (!stackField) return null;
    return (
      summarizedData?._metadata?.group_fields?.[stackField] ||
      getAvailableCategoryFields().find((f) => f.value === stackField)?.fieldInfo ||
      null
    );
  };

  const isStackByPicklist = () => {
    const fieldInfo = getStackByFieldInfo();
    return Boolean(fieldInfo && isPicklistGroupField(fieldInfo));
  };

  /** Normalize #RGB / #RRGGBB for <input type="color"> */
  const toColorInputHex = (color, fallback = '#36A2EB') => {
    if (typeof color !== 'string') return fallback;
    const t = color.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t;
    if (/^#[0-9A-Fa-f]{3}$/.test(t)) {
      return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`;
    }
    if (t.startsWith('rgba') || t.startsWith('rgb')) {
      return rgbaToHex(t);
    }
    return fallback;
  };

  /**
   * Stack segment labels for color pickers when Stack By is a picklist.
   * Prefer picklist display labels; also include values present in data.
   */
  const getStackSegmentLabelsForColors = () => {
    const stackField = chartConfig.stackByField;
    if (!stackField) return [];

    const fieldInfo = getStackByFieldInfo();
    const labels = new Set();
    const addLabel = (raw) => {
      if (raw == null || raw === '') return;
      const s = String(raw);
      if (isChartGrandTotalCategory(s)) return;
      labels.add(s);
    };

    const picklistValues = fieldInfo?.picklist_values || fieldInfo?.picklist_values_data || [];
    if (Array.isArray(picklistValues)) {
      picklistValues.forEach((pv) => {
        if (!pv) return;
        addLabel(pv.value_label ?? pv.label ?? pv.value ?? pv.value_key ?? pv.key);
      });
    }

    const stackBucket = summarizedData?.[stackField];
    if (stackBucket && typeof stackBucket === 'object') {
      Object.keys(stackBucket).forEach(addLabel);
    }

    if (Array.isArray(previewData) && fieldInfo) {
      const tableName = fieldInfo.object_name || 'main';
      const aliased = `${tableName}_${fieldInfo.field_name || stackField}`;
      previewData.forEach((row) => {
        const fieldData = row[aliased] || row[fieldInfo.field_name] || row[stackField];
        if (fieldData && typeof fieldData === 'object') {
          addLabel(fieldData.displayValue || fieldData.value || fieldData.label);
        } else {
          addLabel(fieldData);
        }
      });
    }

    return Array.from(labels);
  };

  const getStackSegmentColor = (label, index) => {
    if (chartConfig.fieldColors?.[label]) {
      return toColorInputHex(chartConfig.fieldColors[label]);
    }
    if (chartConfig.legendColors?.[label]) {
      return toColorInputHex(chartConfig.legendColors[label]);
    }
    const fieldInfo = getStackByFieldInfo();
    const plMap = buildPicklistValueColorMap(fieldInfo?.picklist_values);
    if (plMap.size > 0) {
      const key = String(label);
      if (plMap.has(key)) return toColorInputHex(plMap.get(key));
      for (const [k, v] of plMap.entries()) {
        if (String(k).trim() === key.trim()) return toColorInputHex(v);
      }
    }
    return getDefaultHexColorForLabel(index);
  };

  const resetStackSegmentColor = (label) => {
    setChartConfig((prev) => {
      const newFieldColors = { ...prev.fieldColors };
      delete newFieldColors[label];
      return { ...prev, fieldColors: newFieldColors };
    });
  };

  // Set default field selections when summarizedData changes (summary reports only)
  useEffect(() => {
    if (isMatrixReport) return;
    if (summarizedData && Object.keys(summarizedData).length > 0 && summarizedData._metadata) {
      const metadata = summarizedData._metadata;

      if (!chartConfig.selectedCategoryField && metadata.group_fields) {
        const firstCategoryField = Object.keys(metadata.group_fields)[0];
        if (firstCategoryField) {
          setChartConfig(prev => ({ ...prev, selectedCategoryField: firstCategoryField }));
        }
      }

      if ((!chartConfig.selectedValueFields || chartConfig.selectedValueFields.length === 0) && metadata.summarized_fields) {
        const firstValueField =
          getAvailableValueFields().find((f) => f.value !== 'record_count' && !f.fieldInfo?.is_derived)
            ?.value ||
          Object.keys(metadata.summarized_fields).find(
            (k) => k !== 'record_count' && !metadata.summarized_fields[k]?.is_derived
          );
        if (firstValueField) {
          setChartConfig(prev => {
            const availableFields = getAvailableValueFields();
            const fieldIndex = availableFields.findIndex(f => f.value === firstValueField);
            const defaultColor = getColorForField(firstValueField, fieldIndex);

            return {
              ...prev,
              selectedValueFields: [firstValueField],
              fieldColors: {
                ...prev.fieldColors,
                [firstValueField]: defaultColor
              }
            };
          });
        }
      }
    }
  }, [summarizedData, chartConfig.selectedCategoryField, chartConfig.selectedValueFields, isMatrixReport]);

  // Generate chart preview data
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      try {
        const chartData = processChartData();
        // Preview data is now handled by the parent component
        console.log('Generated chart data:', chartData);
      } catch (error) {
        console.error('Error generating chart data:', error);
        // Preview data is now handled by the parent component
      } finally {
        setIsLoading(false);
      }
    }
  }, [chartConfig.chartType, chartConfig.selectedCategoryField, chartConfig.selectedValueFields, chartConfig.seriesTypes, chartConfig.title, chartConfig.xAxisLabel, chartConfig.yAxisLabel, chartConfig.fieldColors, chartConfig.showMatrixColumnTotal, chartConfig.chartFullHeight, chartConfig.stackTo100Percent, chartConfig.showTooltips, chartConfig.showLabels, chartConfig.labelColor, isOpen, chartValues, chartLabels, xAxisLabel, yAxisLabel, chartTitle, summarizedData]);

  // Extract data from enhanced summarizedData structure
  const extractDataFromSummarizedData = () => {
    if (!summarizedData || Object.keys(summarizedData).length === 0) {
      return null;
    }

    // Get the first group field and first summarized field from metadata
    const metadata = summarizedData._metadata;
    if (!metadata || !metadata.group_fields || !metadata.summarized_fields) {
      return null;
    }

    // Use selected fields if available, otherwise fall back to first available fields
    const selectedCategoryField = chartConfig.selectedCategoryField || Object.keys(metadata.group_fields)[0];
    const selectedValueFields = chartConfig.selectedValueFields && chartConfig.selectedValueFields.length > 0
      ? chartConfig.selectedValueFields
      : [Object.keys(metadata.summarized_fields)[0]].filter(Boolean);

    if (!selectedCategoryField || !selectedValueFields || selectedValueFields.length === 0) {
      console.log('Missing required fields:', { selectedCategoryField, selectedValueFields });
      return null;
    }

    console.log('Using selected fields:', { selectedCategoryField, selectedValueFields });
    console.log('Available summarized data keys:', Object.keys(summarizedData));
    console.log('Group data for', selectedCategoryField, ':', summarizedData[selectedCategoryField]);
    console.log('Metadata:', metadata);

    const groupData = summarizedData[selectedCategoryField];
    if (!groupData) {
      console.log('No group data found for field:', selectedCategoryField);
      return null;
    }

    const labels = [];
    const datasets = [];

    // Get all unique group values (categories)
    const allGroupValues = new Set();
    Object.keys(groupData).forEach(groupValue => {
      allGroupValues.add(groupValue);
    });

    labels.push(...Array.from(allGroupValues));

    const groupFieldInfo = metadata.group_fields[selectedCategoryField];

    // Create datasets for each selected value field
    selectedValueFields.forEach((valueFieldName, valueIdx) => {
      let fieldInfo;
      let isVirtualRecordCount = false;

      if (valueFieldName === 'record_count') {
        // Handle virtual record count field
        fieldInfo = {
          field_label: 'Record Count',
          field_name: 'record_count',
          aggregation: 'count',
          data_type: 'number',
          is_virtual: true
        };
        isVirtualRecordCount = true;
      } else {
        fieldInfo =
          metadata.summarized_fields[valueFieldName] ||
          resolveSummarizedFieldMeta(
            valueFieldName,
            metadata,
            selectedFields,
            summarizedFields
          );
        if (!fieldInfo) return;
      }

      const fieldColor = getFieldColor(valueFieldName, valueIdx);
      const perCategory = getCategoryAxisColorsForChart(labels, groupFieldInfo, fieldColor, valueIdx);
      const ct = (chartConfig.chartType || '').toLowerCase();

      const dataset = {
        label: `${fieldInfo.field_label || valueFieldName} (${fieldInfo.aggregation?.toUpperCase() || 'SUM'})`,
        data: [],
        borderWidth: ct === 'line' || ct === 'area' ? 2 : 1,
        fill: ct === 'area'
      };

      if (ct === 'line' && perCategory) {
        dataset.backgroundColor = 'rgba(0,0,0,0)';
        dataset.borderColor = fieldColor;
        dataset.pointBackgroundColor = perCategory;
        dataset.pointBorderColor = perCategory;
      } else if (ct === 'area' && perCategory) {
        dataset.backgroundColor = perCategory.map((c) => appendAlphaIfHex(c, '40'));
        dataset.borderColor = fieldColor;
        dataset.pointBackgroundColor = perCategory;
        dataset.pointBorderColor = perCategory;
      } else {
        dataset.backgroundColor = perCategory || fieldColor;
        dataset.borderColor =
          ct === 'line' || ct === 'area' ? fieldColor : (perCategory || fieldColor);
      }

      // Extract values for this field across all categories
      labels.forEach(category => {
        let parsedValue = 0;

        if (isVirtualRecordCount) {
          // For record count, calculate the actual number of records in each group
          const groupField = metadata.group_fields[selectedCategoryField];
          parsedValue = calculateRecordCountForGroup(category, groupField);
        } else {
          // Handle regular summarized fields
          const groupSummary = groupData[category];
          if (groupSummary && groupSummary[valueFieldName]) {
            const rawValue = groupSummary[valueFieldName].value;
            console.log('Raw value for', valueFieldName, 'in', category, ':', rawValue);

            if (typeof rawValue === 'number') {
              parsedValue = rawValue;
            } else if (typeof rawValue === 'string') {
              if (rawValue === 'N/A' || rawValue.trim() === '') {
                parsedValue = 0;
              } else {
                let cleanValue = rawValue.replace(/[₹$,\s]/g, '').trim();
                if (cleanValue.endsWith('%')) {
                  cleanValue = cleanValue.replace('%', '');
                }
                parsedValue = parseFloat(cleanValue) || 0;
              }
            }
          }
        }

        dataset.data.push(parsedValue);
      });

      datasets.push(dataset);
      console.log('Created dataset for field', valueFieldName, ':', dataset);
    });

    console.log('Final labels:', labels);
    console.log('Final datasets:', datasets);

    // Use the labels from chartConfig (which will have auto-generated defaults if not customized)
    const result = {
      labels,
      datasets,
      groupFieldInfo,
      selectedValueFields,
      xAxisLabel: chartConfig.xAxisLabel || 'Categories',
      yAxisLabel: chartConfig.yAxisLabel || 'Values'
    };

    console.log('Extracted data result:', result);
    return result;
  };

  // Simple data processing using props directly
  const processChartData = () => {
    console.log('Processing chart data:', {
      chartValues,
      chartLabels,
      xAxisLabel,
      yAxisLabel,
      chartTitle,
      summarizedData: !!summarizedData && Object.keys(summarizedData).length > 0
    });

    // Try to extract data from enhanced summarizedData first
    const extractedData = extractDataFromSummarizedData();
    if (extractedData) {
      console.log('Using extracted data from summarizedData:', extractedData);

      // Ensure we have valid arrays for labels and datasets
      const labels = Array.isArray(extractedData.labels) ? extractedData.labels : [];
      const datasets = Array.isArray(extractedData.datasets) ? extractedData.datasets : [];

      console.log('Processed labels:', labels);
      console.log('Processed datasets:', datasets);

      return {
        type: chartConfig.chartType,
        labels: labels,
        data: datasets, // Use datasets directly
        datasets: datasets, // Also provide datasets for ChartDisplay
        title: chartConfig.title || chartTitle,
        xAxisTitle: extractedData.xAxisLabel,
        yAxisTitle: extractedData.yAxisLabel,
        fieldInfo: {
          groupField: extractedData.groupFieldInfo,
          selectedValueFields: extractedData.selectedValueFields
        }
      };
    }

    // No summarized data: show empty chart (no sample/dummy points)
    const hasProps =
      (Array.isArray(chartLabels) && chartLabels.length > 0) ||
      (Array.isArray(chartValues) && chartValues.length > 0);
    if (hasProps) {
      const labels = chartLabels;
      const data = chartValues;
      const fromPropsDatasets = [{
        label: 'Data',
        data,
        backgroundColor: '#36A2EB',
        borderColor: '#36A2EB',
        borderWidth: 1
      }];
      return {
        type: chartConfig.chartType,
        labels,
        data: fromPropsDatasets,
        datasets: fromPropsDatasets,
        title: chartConfig.title || chartTitle || '',
        xAxisTitle: chartConfig.xAxisLabel || xAxisLabel || 'Categories',
        yAxisTitle: chartConfig.yAxisLabel || yAxisLabel || 'Values'
      };
    }

    return {
      type: chartConfig.chartType,
      labels: [],
      data: [],
      datasets: [],
      title: chartConfig.title || chartTitle || '',
      xAxisTitle: chartConfig.xAxisLabel || xAxisLabel || 'Categories',
      yAxisTitle: chartConfig.yAxisLabel || yAxisLabel || 'Values'
    };
  };

  // Handle save chart
  const handleSaveChart = async () => {
    if (isSaving) {
      return;
    }
    if (!chartConfig.title.trim()) {
      alert('Please enter a chart title');
      return;
    }
    if (showStackByChartConfig) {
      if (!chartConfig.selectedCategoryField) {
        alert('Please select an X-axis (category) field');
        return;
      }
      if (!chartConfig.stackByField) {
        alert('Please select a Stack By field (second grouping dimension)');
        return;
      }
      const measure =
        chartConfig.matrixMeasureField || chartConfig.selectedValueFields?.[0];
      if (!measure) {
        alert('Please select a measure for the chart');
        return;
      }
    }
    setIsSaving(true);



    // Get current user information for audit fields
    const getCurrentUser = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = jwt_decode(token);
          return {
            id: decoded.id,
            username: decoded.username
          };
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
      return { id: null, username: 'Unknown' };
    };

    const currentUser = getCurrentUser();

    // Get the processed chart data first
    const processedChartData = processChartData();
    console.log('chartConfig.chartType:', chartConfig.chartType);
    // Create chart data in the format expected by ChartDisplay component
    const resolvedEngine =
      editingChart?.chartEngine ||
      (chartConfig.chartType === 'scatter' ? 'chartjs' : 'echarts');

    const chartData = {
      id: editingChart ? editingChart.id : `chart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Use existing ID if editing, generate unique ID for new charts
      title: chartConfig.title,
      chartType: chartConfig.chartType,
      chartEngine: resolvedEngine,
      reportId: reportData?.id,
      createdAt: editingChart ? editingChart.createdAt : new Date().toISOString(), // Keep original creation date if editing
      updatedAt: new Date().toISOString(), // Always update the modification date
      // Map the selected fields based on chart type
      ...(chartConfig.chartType === 'scatter' ? {
        // Scatter chart specific fields
        scatterXAxis: chartConfig.scatterXAxis,
        scatterYAxis: chartConfig.scatterYAxis,
        scatterGroupField: chartConfig.scatterGroupField,
        scatterXAxisLabel: chartConfig.scatterXAxisLabel,
        scatterYAxisLabel: chartConfig.scatterYAxisLabel,
      } : chartConfig.chartType === 'donut' ? {
        // Donut chart specific fields - save donut-specific properties
        pieValueField: chartConfig.pieValueField,
        pieSliceField: chartConfig.pieSliceField,
        showValues: chartConfig.showValues,
        showPercentages: chartConfig.showPercentages,
        showTotal: chartConfig.showTotal,
        legendColors: chartConfig.legendColors,
        valueFieldType: chartConfig.valueFieldType,
        // Also save standard structure for compatibility
        xAxisField: chartConfig.pieSliceField,
        yAxisFields: chartConfig.pieValueField ? [chartConfig.pieValueField] : [],
        xAxisLabel: chartConfig.pieSliceField,
        yAxisLabel: chartConfig.pieValueField,
      } : chartConfig.chartType === 'funnel' ? {
        funnelStageField:
          chartConfig.funnelStageField || chartConfig.pieSliceField,
        funnelValueField:
          chartConfig.funnelValueField || chartConfig.pieValueField,
        pieValueField: chartConfig.funnelValueField || chartConfig.pieValueField,
        pieSliceField: chartConfig.funnelStageField || chartConfig.pieSliceField,
        xAxisField: chartConfig.funnelStageField || chartConfig.pieSliceField,
        yAxisFields: chartConfig.funnelValueField
          ? [chartConfig.funnelValueField]
          : chartConfig.pieValueField
            ? [chartConfig.pieValueField]
            : [],
        showTooltips: chartConfig.showTooltips,
        showLabels: chartConfig.showLabels,
        showValues: chartConfig.showValues,
        showPercentages: chartConfig.showPercentages,
        showTotal: chartConfig.showTotal,
        labelColor: chartConfig.labelColor,
        legendPosition: chartConfig.legendPosition,
        valueFieldType: chartConfig.valueFieldType,
      } : {
        // Regular chart fields (bar, line, etc.)
        xAxisField: chartConfig.selectedCategoryField,
        yAxisFields: showStackByChartConfig
          ? chartConfig.matrixMeasureField
            ? [chartConfig.matrixMeasureField]
            : chartConfig.selectedValueFields
          : chartConfig.selectedValueFields,
        yAxisField: chartConfig.matrixMeasureField || chartConfig.selectedValueFields?.[0],
        stackByField: chartConfig.stackByField,
        matrixLineField: chartConfig.matrixLineField || '',
        matrixChart: isMatrixReport,
        showMatrixColumnTotal: isMatrixReport
          ? !!chartConfig.showMatrixColumnTotal
          : false,
        chartFullHeight: !!chartConfig.chartFullHeight,
        stackTo100Percent: !!chartConfig.stackTo100Percent,
        xAxisLabel: chartConfig.xAxisLabel,
        yAxisLabel: chartConfig.yAxisLabel,
        matrixMeasureField: chartConfig.matrixMeasureField || '',
        ...(chartConfig.chartType === 'mixed' || chartConfig.matrixLineField
          ? { seriesTypes: chartConfig.seriesTypes || {} }
          : {}),
      }),
      legendPosition: chartConfig.legendPosition,
      // Store custom field colors (not applicable for scatter charts and donut charts)
      ...(chartConfig.chartType !== 'scatter' && chartConfig.chartType !== 'donut' ? {
        fieldColors: chartConfig.fieldColors
      } : {}),
      // Store display options
      showTooltips: chartConfig.showTooltips,
      showLabels: chartConfig.showLabels,
      labelColor: chartConfig.labelColor,
      // Store layout options
      sortOrder: chartConfig.sortOrder,
      columnSpan: chartConfig.columnSpan,
      derivedMetrics: chartConfig.derivedMetrics || [],
      showPeriodGrowth: !!chartConfig.showPeriodGrowth,
      // Store the processed chart data for reference
      chartData: processedChartData,
      // Store the original data for regeneration
      data: {
        labels: processedChartData?.labels || [],
        values: processedChartData?.datasets?.[0]?.data || []
      },
      // Add audit fields based on whether this is a new chart or editing existing
      ...(editingChart ? {
        // For editing: preserve original creation fields, update modification fields
        createdby: editingChart.createdby,
        createddate: editingChart.createddate,
        createdbyid: editingChart.createdbyid,
        lastmodifiedby: currentUser.username,
        lastmodifieddate: new Date().toISOString(),
        lastmodifiedbyid: currentUser.id
      } : {
        // For new chart: set all audit fields to current user
        createdby: currentUser.username,
        createddate: new Date().toISOString(),
        createdbyid: currentUser.id,
        lastmodifiedby: currentUser.username,
        lastmodifieddate: new Date().toISOString(),
        lastmodifiedbyid: currentUser.id
      })
    };

    console.log('Saving chart data:', chartData);

    // Show the charts array that will be sent when saving the report
    console.log('=== CHARTS ARRAY FOR REPORT SAVE ===');
    console.log('Array of charts to be embedded in report data:');
    console.log(JSON.stringify([
      chartData,
      // Example of additional charts in the array:
      // {
      //   "id": "chart_1234567891",
      //   "title": "Employee Age Distribution",
      //   "chartType": "donut",
      //   "xAxisField": "department",
      //   "yAxisFields": ["age"],
      //   "xAxisLabel": "Department",
      //   "yAxisLabel": "Average Age",
      //   "fieldColors": { "age": "#FF6384" },
      //   "showTooltips": true,
      //   "showLabels": true,
      //   "labelColor": "#000000"
      // }
    ], null, 2));
    console.log('=== END CHARTS ARRAY ===');
    try {
      await Promise.resolve(
        onSaveChart(chartData, editingChart ? 'update' : 'create')
      );
      onClose();
    } catch (error) {
      console.error('Error saving chart:', error);
      alert('Failed to save chart. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render table preview
  const renderTablePreview = () => {
    // Try to use enhanced summarizedData first
    const extractedData = extractDataFromSummarizedData();
    if (extractedData) {
      return (
        <div className="table-preview">
          <div className="table-info">
            <h5>Data from Enhanced SummarizedData</h5>
            <p><strong>Group Field:</strong> {extractedData.groupFieldInfo?.field_label || 'N/A'}</p>
            <p><strong>Summarized Field:</strong> {extractedData.summarizedFieldInfo?.field_label || 'N/A'}</p>
            <p><strong>Aggregation:</strong> {extractedData.summarizedFieldInfo?.aggregation?.toUpperCase() || 'N/A'}</p>
            <p><strong>Data Type:</strong> {extractedData.summarizedFieldInfo?.data_type || 'N/A'}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>{extractedData.xAxisLabel}</th>
                <th>{extractedData.yAxisLabel}</th>
              </tr>
            </thead>
            <tbody>
              {extractedData.labels.map((label, index) => (
                <tr key={index}>
                  <td>{label}</td>
                  <td>{extractedData.values[index]?.toLocaleString() || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Fallback to simple props
    if (!chartLabels || !chartValues || chartLabels.length === 0) {
      return <div className="no-data">No table data available</div>;
    }

    return (
      <div className="table-preview">
        <table>
          <thead>
            <tr>
              <th>{xAxisLabel || 'Category'}</th>
              <th>{yAxisLabel || 'Value'}</th>
            </tr>
          </thead>
          <tbody>
            {chartLabels.map((label, index) => (
              <tr key={index}>
                <td>{label}</td>
                <td>{chartValues[index] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleSeriesTypeChange = (fieldName, seriesType) => {
    setChartConfig((prev) => ({
      ...prev,
      seriesTypes: {
        ...prev.seriesTypes,
        [fieldName]: seriesType,
      },
    }));
  };

  // Render chart based on type
  const renderChart = () => {
    if (previewEngine === 'echarts' && chartConfig.chartType !== 'scatter') {
      return (
        <div className="chart-preview-container" style={{ minHeight: 320 }}>
          <ChartRenderer
            key={`report-chart-preview-${reportConversionBaseCode || 'nc'}`}
            chart={buildPreviewChartObject()}
            isPreview
            summarizedData={summarizedData}
            previewData={previewData}
            selectedGroups={selectedGroups}
            selectedColumnGroups={selectedColumnGroups}
            selectedFields={selectedFields}
            summarizedFields={summarizedFields}
            matrixDateGranularity={matrixDateGranularity}
            baseCurrencyCode={reportConversionBaseCode}
            exchangeRateLookup={reportConversionLookup}
            multiCurrencyEnabled={multiCurrencyEnabled}
          />
        </div>
      );
    }

    const chartData = processChartData();
    if (!chartData) return null;

    console.log('Rendering chart with processed data:', chartData);

    // Check if we have valid data
    if (!chartData.labels || chartData.labels.length === 0 || !chartData.datasets || chartData.datasets.length === 0) {
      return (
        <div className="chart-placeholder">
          <div className="placeholder-icon">⚠️</div>
          <p>No data available</p>
          <small>Please select fields and generate preview data</small>
        </div>
      );
    }

    // Chart options for all chart types
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: chartConfig.legendPosition || 'top',
          display: chartConfig.chartType !== 'donut'
        },
        title: {
          display: false // We'll show title separately
        },
        tooltip: {
          enabled: chartConfig.showTooltips !== undefined ? chartConfig.showTooltips : true,
          callbacks: {
            label: function (context) {
              const value = context.parsed.y || context.parsed.x;
              return `${context.dataset.label}: ${value.toLocaleString()}`;
            }
          }
        },
        datalabels: {
          display: chartConfig.showLabels !== undefined ? chartConfig.showLabels : false,
          anchor: 'end',
          align: 'top',
          color: chartConfig.labelColor || '#000000',
          font: {
            weight: 'bold',
            size: 12
          },
          formatter: function (value) {
            return value.toLocaleString();
          }
        }
      },
      scales: chartConfig.chartType !== 'donut' && chartConfig.chartType !== 'table' ? {
        x: {
          display: true,
          title: {
            display: true,
            text: chartConfig.xAxisLabel || xAxisLabel || 'Categories'
          }
        },
        y: {
          display: true,
          title: {
            display: true,
            text: chartConfig.yAxisLabel || yAxisLabel || 'Values'
          },
          beginAtZero: true
        }
      } : {}
    };

    // Render different chart types with multiseries support
    switch (chartConfig.chartType) {
      case 'bar':
        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Bar key={`bar-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={options} />
            </div>
          </div>
        );

      case 'horizontalBar':
        // Enhanced options for horizontal bar charts with consistent bar thickness
        const horizontalBarOptions = {
          ...options,
          indexAxis: 'y',
          elements: {
            bar: {
              borderWidth: 1,
              borderSkipped: false,
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                display: true
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                maxTicksLimit: 20 // Limit number of ticks for better spacing
              }
            }
          },
          plugins: {
            ...options.plugins,
            legend: {
              ...options.plugins.legend,
              position: 'bottom'
            }
          }
        };

        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Bar key={`horizontalBar-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={horizontalBarOptions} />
            </div>
          </div>
        );

      case 'line':
        // Enhanced options for line charts
        const lineOptions = {
          ...options,
          elements: {
            line: {
              tension: 0.4, // Smooth curves
              borderWidth: 3,
              fill: false
            },
            point: {
              radius: 4,
              hoverRadius: 6,
              borderWidth: 2
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            ...options.plugins,
            tooltip: {
              ...options.plugins.tooltip,
              mode: 'index',
              intersect: false
            }
          }
        };

        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Line key={`line-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={lineOptions} />
            </div>
          </div>
        );

      case 'scatter':
        return (
          <ScatterChart
            key={`scatter-${chartConfig.chartType}-${chartConfig.title}`}
            chartConfig={chartConfig}
            chartTitle={chartConfig.title || chartTitle}
            summarizedData={summarizedData}
            chartData={chartData}
          />
        );

      case 'donut':
        console.log('Rendering DonutChart with:', {
          chartConfig: chartConfig,
          summarizedData: summarizedData,
          chartData: chartData
        });
        return (
          <DonutChart
            key={`donut-${chartConfig.chartType}-${chartConfig.title}-${JSON.stringify(chartConfig.legendColors)}-${chartConfig._colorUpdate || 0}`}
            chartConfig={chartConfig}
            chartTitle={chartConfig.title || chartTitle}
            summarizedData={summarizedData}
            chartData={chartData}
            previewData={previewData}
            selectedGroups={selectedGroups}
          />
        );

      case 'area':
        // Create proper area chart data with fill configurations
        const areaChartData = {
          ...chartData,
          datasets: chartData.datasets.map((dataset) => {
            const bg = dataset.backgroundColor;
            const withFill = Array.isArray(bg)
              ? bg
              : (typeof bg === 'string' && bg.startsWith('#') && bg.length === 7
                ? appendAlphaIfHex(bg, '40')
                : typeof bg === 'string'
                  ? bg + '40'
                  : bg);
            return {
              ...dataset,
              fill: true,
              tension: 0.4,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 5,
              backgroundColor: withFill,
              borderColor: dataset.borderColor
            };
          })
        };

        // Enhanced options for area charts
        const areaOptions = {
          ...options,
          elements: {
            line: {
              tension: 0.4, // Smooth curves
              borderWidth: 2
            },
            point: {
              radius: 3,
              hoverRadius: 5,
              borderWidth: 2
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            ...options.plugins,
            tooltip: {
              ...options.plugins.tooltip,
              mode: 'index',
              intersect: false
            }
          },
          scales: {
            ...options.scales,
            x: {
              ...options.scales?.x,
              stacked: false // Allow overlapping areas
            },
            y: {
              ...options.scales?.y,
              stacked: false, // Allow overlapping areas
              beginAtZero: true
            }
          }
        };

        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Line key={`area-${chartConfig.chartType}-${chartConfig.title}`} data={areaChartData} options={areaOptions} />
            </div>
          </div>
        );

      case 'table':
        return (
          <div key={`table-${chartConfig.chartType}-${chartConfig.title}`}>
            {renderTablePreview()}
          </div>
        );

      default:
        return (
          <div className="chart-preview-container">
            <div className="chart-title">{chartConfig.title || chartTitle}</div>
            <div className="chart-render" style={{ height: '300px' }}>
              <Bar key={`default-${chartConfig.chartType}-${chartConfig.title}`} data={chartData} options={options} />
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chart-modal-overlay">
      <div className="chart-modal">
        <div className="chart-modal-header">
          <h3>{editingChart ? 'Edit Chart' : 'Add Chart'}</h3>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="chart-modal-content">
          <div className="chart-config-section">
            <div className="form-group">
              <label>Chart Title *</label>
              <input
                type="text"
                value={chartConfig.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter chart title (required for saving)"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Chart Type *</label>
              <div className="chart-type-grid">
                {chartTypes.map(type => (
                  <div
                    key={type.value}
                    className={`chart-type-option ${chartConfig.chartType === type.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange('chartType', type.value)}
                  >
                    <type.icon className="chart-type-icon" />
                    <span className="chart-type-label">{type.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Legend Position - Hide for donut charts as they have their own control */}
            {chartConfig.chartType !== 'donut' && (
              <div className="form-group">
                <label>Legend Position</label>
                <select
                  value={chartConfig.legendPosition || 'top'}
                  onChange={(e) => handleInputChange('legendPosition', e.target.value)}
                  className="form-select"
                >
                  <option value="top">Top</option>
                  <option value="bottom">Bottom</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
                <small className="form-help">Choose where to display the legend on the chart.</small>
              </div>
            )}

            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                min="1"
                value={chartConfig.sortOrder}
                onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 1)}
                placeholder="Enter sort order (1, 2, 3...)"
                className="form-input"
              />
              <small className="form-help">Lower numbers appear first in the report</small>
            </div>

            <div className="form-group">
              <label>Column Span</label>
              <select
                value={chartConfig.columnSpan}
                onChange={(e) => handleInputChange('columnSpan', parseInt(e.target.value))}
                className="form-select"
              >
                <option value={1}>1 Column (Half Width)</option>
                <option value={2}>2 Columns (Full Width)</option>
              </select>
              <small className="form-help">Choose how much horizontal space the chart should take</small>
            </div>

            {/* Scatter Chart Configuration */}
            {chartConfig.chartType === 'scatter' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Group Field (Optional)</label>
                  <select
                    value={chartConfig.scatterGroupField || ''}
                    onChange={(e) => handleInputChange('scatterGroupField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">No grouping (single series)</option>
                    {getAvailableCategoryFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose a field to group data points (e.g., Department). Each group will have different colored points.</small>
                </div>

                <div className="form-group">
                  <label>X-Axis Field *</label>
                  <select
                    value={chartConfig.scatterXAxis || ''}
                    onChange={(e) => handleInputChange('scatterXAxis', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select X-axis field</option>
                    {getAvailableValueFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose which field to display on X-axis (e.g., Age). Values will range from 0 to maximum.</small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Field *</label>
                  <select
                    value={chartConfig.scatterYAxis || ''}
                    onChange={(e) => handleInputChange('scatterYAxis', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select Y-axis field</option>
                    {getAvailableValueFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose which field to display on Y-axis (e.g., Salary). Values will range from 0 to maximum.</small>
                </div>

                <div className="form-group">
                  <label>X-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.scatterXAxisLabel}
                    onChange={(e) => handleInputChange('scatterXAxisLabel', e.target.value)}
                    className="form-input"
                    placeholder="Auto-generated from selected field"
                  />
                  <small className="form-help">Custom label for X-axis. Auto-generated from selected field, but you can customize it.</small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.scatterYAxisLabel}
                    onChange={(e) => handleInputChange('scatterYAxisLabel', e.target.value)}
                    className="form-input"
                    placeholder="Auto-generated from selected field"
                  />
                  <small className="form-help">Custom label for Y-axis. Auto-generated from selected field, but you can customize it.</small>
                </div>
              </>
            )}

            {/* Funnel Chart Configuration */}
            {chartConfig.chartType === 'funnel' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Value</label>
                  <select
                    value={chartConfig.funnelValueField || chartConfig.pieValueField}
                    onChange={(e) => {
                      handleInputChange('funnelValueField', e.target.value);
                      handleInputChange('pieValueField', e.target.value);
                    }}
                    className="form-select"
                  >
                    <option value="">Select a value field</option>
                    {getAvailableValueFields().map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Stage field</label>
                  <select
                    value={chartConfig.funnelStageField || chartConfig.pieSliceField}
                    onChange={(e) => {
                      handleInputChange('funnelStageField', e.target.value);
                      handleInputChange('pieSliceField', e.target.value);
                    }}
                    className="form-select"
                  >
                    <option value="">Select a stage field</option>
                    {getAvailableCategoryFields().map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Legend Position</label>
                  <select
                    value={chartConfig.legendPosition}
                    onChange={(e) => handleInputChange('legendPosition', e.target.value)}
                    className="form-select"
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </>
            )}

            {/* Donut Chart Configuration */}
            {chartConfig.chartType === 'donut' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Value</label>
                  <select
                    value={chartConfig.pieValueField}
                    onChange={(e) => handleInputChange('pieValueField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a value field</option>
                    {getAvailableValueFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Select the value field to use for the donut chart</small>
                </div>

                <div className="form-group">
                  <label>Sliced By</label>
                  <select
                    value={chartConfig.pieSliceField}
                    onChange={(e) => handleInputChange('pieSliceField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a field to slice by</option>
                    {getAvailableCategoryFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Select the field to use for slicing the donut chart</small>
                </div>

                <div className="form-group">
                  <label>Legend Position</label>
                  <select
                    value={chartConfig.legendPosition}
                    onChange={(e) => handleInputChange('legendPosition', e.target.value)}
                    className="form-select"
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </>
            )}

            {showStackByChartConfig &&
              chartConfig.chartType !== 'scatter' &&
              chartConfig.chartType !== 'donut' &&
              chartConfig.chartType !== 'funnel' &&
              summarizedData &&
              Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>X-Axis *</label>
                  <select
                    value={chartConfig.selectedCategoryField}
                    onChange={(e) => {
                      const next = e.target.value;
                      handleInputChange('selectedCategoryField', next);
                      if (chartConfig.stackByField === next) {
                        const other = getAvailableCategoryFields().find(
                          (f) => f.value !== next
                        );
                        if (other) handleInputChange('stackByField', other.value);
                      }
                    }}
                    className="form-select"
                  >
                    <option value="">Select X-axis field</option>
                    {getAvailableCategoryFields().map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    {isMatrixReport
                      ? 'Row or column group (e.g. Opportunity Owner or Stage).'
                      : 'Primary row group (e.g. Stage).'}
                  </small>
                </div>

                <div className="form-group">
                  <label>Y-Axis (Measure) *</label>
                  <select
                    value={chartConfig.matrixMeasureField}
                    onChange={(e) => {
                      handleInputChange('matrixMeasureField', e.target.value);
                      handleInputChange('selectedValueFields', [e.target.value]);
                    }}
                    className="form-select"
                  >
                    <option value="">Select measure</option>
                    {getAvailableMatrixMeasures().map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Stack By *</label>
                  <select
                    value={chartConfig.stackByField}
                    onChange={(e) => handleInputChange('stackByField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select stack dimension</option>
                    {getAvailableStackByFields().map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    {isMatrixReport
                      ? 'Color segments in each bar (e.g. Stage when X-axis is Owner).'
                      : 'Second row group — color segments in each bar (e.g. Owner when X-axis is Stage).'}
                  </small>
                </div>

                {isMatrixReport && (
                <div className="form-group">
                  <label>Line overlay (optional)</label>
                  <select
                    value={chartConfig.matrixLineField || ''}
                    onChange={(e) => {
                      const next = e.target.value;
                      setChartConfig((prev) => {
                        const updates = { matrixLineField: next };
                        if (next) {
                          updates.chartType = 'mixed';
                          updates.seriesTypes = {
                            ...(prev.seriesTypes || {}),
                            [next]: 'line',
                          };
                          if (
                            prev.matrixMeasureField === next ||
                            prev.selectedValueFields?.[0] === next
                          ) {
                            updates.matrixMeasureField = 'record_count';
                            updates.selectedValueFields = ['record_count'];
                          }
                        } else if (prev.chartType === 'mixed') {
                          updates.chartType = 'stackedColumn';
                        }
                        return { ...prev, ...updates };
                      });
                    }}
                    className="form-select"
                  >
                    <option value="">None</option>
                    {getAvailableMatrixLineFields().map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    Overlay a rate or formula column as a line (e.g. Win Rate %) on stacked bars.
                  </small>
                </div>
                )}

                <div className="form-group">
                  <label>X-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.xAxisLabel}
                    onChange={(e) => handleInputChange('xAxisLabel', e.target.value)}
                    className="form-input"
                  />
                  <small className="form-help">
                    Label for X-axis. Auto-generated based on selected field, but you can customize it.
                  </small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.yAxisLabel}
                    onChange={(e) => handleInputChange('yAxisLabel', e.target.value)}
                    className="form-input"
                  />
                  <small className="form-help">
                    Label for Y-axis. Auto-generated based on selected measure, but you can customize it.
                  </small>
                </div>

                {chartConfig.stackByField && isStackByPicklist() && (
                  <div className="form-group">
                    <label className="form-label">Stack Segment Colors</label>
                    <div className="chart-display-options">
                      <div className="options-container">
                        <table className="options-table legend-colors-table">
                          <tbody>
                            {getStackSegmentLabelsForColors().map((label, index) => {
                              const currentColor = getStackSegmentColor(label, index);
                              const isCustomColor = Boolean(chartConfig.fieldColors?.[label]);
                              return (
                                <tr key={label} className="option-row">
                                  <td className="number-cell">
                                    <span className="legend-number">{index + 1}</span>
                                  </td>
                                  <td className="label-cell">
                                    <label className="option-label">{label}</label>
                                  </td>
                                  <td className="color-cell">
                                    <div className="color-controls">
                                      <input
                                        type="color"
                                        value={currentColor}
                                        onChange={(e) =>
                                          handleFieldColorChange(label, e.target.value)
                                        }
                                        className="color-picker"
                                        title={`Choose color for ${label}`}
                                      />
                                      {isCustomColor && (
                                        <button
                                          type="button"
                                          onClick={() => resetStackSegmentColor(label)}
                                          className="reset-color-btn"
                                          title="Reset to picklist / default color"
                                        >
                                          ↺
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <small className="form-help">
                        Colors for each Stack By value. Picklist path colors are loaded when available; change only if you want a different color on the chart.
                      </small>
                    </div>
                  </div>
                )}
              </>
            )}

            {!showStackByChartConfig &&
              chartConfig.chartType !== 'scatter' &&
              chartConfig.chartType !== 'donut' &&
              chartConfig.chartType !== 'funnel' &&
              summarizedData &&
              Object.keys(summarizedData).length > 0 && (
              <>
                <div className="form-group">
                  <label>Category Field (X-Axis) *</label>
                  <select
                    value={chartConfig.selectedCategoryField}
                    onChange={(e) => handleInputChange('selectedCategoryField', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select a category field</option>
                    {getAvailableCategoryFields().map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">Choose which field to group data by (X-axis categories)</small>
                </div>

                <div className="form-group">
                  <label>Value Fields (Y-Axis) *</label>
                  <div className="multi-select-container">
                    {getAvailableValueFields().map(field => {
                      const isSelected = chartConfig.selectedValueFields?.includes(field.value);
                      const fieldColor = getFieldColor(field.value, getAvailableValueFields().indexOf(field));
                      return (
                        <div key={field.value} className="multi-select-option">
                          <table className="field-option-table">
                            <tbody>
                              <tr>
                                <td className="checkbox-cell">
                                  <input
                                    type="checkbox"
                                    checked={isSelected || false}
                                    onChange={() => handleValueFieldToggle(field.value)}
                                    className="checkbox-input"
                                    id={`field-${field.value}`}
                                  />
                                </td>
                                <td className="label-cell">
                                  <label htmlFor={`field-${field.value}`} className="field-label">
                                    {field.label}
                                  </label>
                                </td>
                                <td className="color-picker-cell">
                                  {isSelected && (
                                    <input
                                      type="color"
                                      value={fieldColor}
                                      onChange={(e) => handleFieldColorChange(field.value, e.target.value)}
                                      className="color-picker"
                                      title={`Choose color for ${field.label}`}
                                    />
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                  <small className="form-help">
                    Choose one or more aggregated fields to display as values (Y-axis). Multiple fields will create grouped bars. Click the color picker to customize colors.
                  </small>
                </div>

                {chartConfig.selectedCategoryField &&
                  chartConfig.selectedValueFields?.length > 0 && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!chartConfig.showPeriodGrowth}
                        onChange={(e) => handlePeriodGrowthToggle(e.target.checked)}
                      />
                      {' '}
                      Show period-over-period growth % (vs previous category)
                    </label>
                    <small className="form-help">
                      Computes growth from the first selected value field along{' '}
                      {chartConfig.selectedCategoryField}
                      {chartConfig.showPeriodGrowth && chartConfig.selectedValueFields?.[0]
                        ? ` → adds ${growthMetricId(chartConfig.selectedValueFields[0])} to the chart`
                        : ''}
                    </small>
                  </div>
                )}

                {chartConfig.chartType === 'mixed' &&
                  chartConfig.selectedValueFields?.length > 0 && (
                    <div className="form-group">
                      <label>Series type per field</label>
                      {chartConfig.selectedValueFields.map((fieldName) => {
                        const fieldMeta = getAvailableValueFields().find(
                          (f) => f.value === fieldName
                        );
                        return (
                          <div key={fieldName} className="d-flex align-items-center gap-2 mb-2">
                            <span style={{ minWidth: 120 }}>
                              {fieldMeta?.label || fieldName}
                            </span>
                            <select
                              className="form-select form-select-sm"
                              value={chartConfig.seriesTypes?.[fieldName] || 'bar'}
                              onChange={(e) =>
                                handleSeriesTypeChange(fieldName, e.target.value)
                              }
                            >
                              <option value="bar">Bar</option>
                              <option value="line">Line</option>
                            </select>
                          </div>
                        );
                      })}
                      <small className="form-help">
                        Use bar for amounts (left axis) and line for rates or percentages (right axis), e.g. Revenue + Win Rate %.
                      </small>
                    </div>
                  )}

                {/* Custom Axis Labels */}
                <div className="form-group">
                  <label>X-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.xAxisLabel}
                    onChange={(e) => handleInputChange('xAxisLabel', e.target.value)}
                    className="form-input"
                  />
                  <small className="form-help">Label for X-axis. Auto-generated based on selected field, but you can customize it.</small>
                </div>

                <div className="form-group">
                  <label>Y-Axis Label</label>
                  <input
                    type="text"
                    value={chartConfig.yAxisLabel}
                    onChange={(e) => handleInputChange('yAxisLabel', e.target.value)}
                    className="form-input"
                  />
                  <small className="form-help">Label for Y-axis. Auto-generated based on selected field and aggregation, but you can customize it.</small>
                </div>

              </>
            )}

            {/* Chart Display Options - Available for all chart types */}
            <div className="form-group">
              <label>Chart Display Options</label>
              <div className="display-options">
                <table className="options-table">
                  <tbody>
                    <tr className="option-row">
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={chartConfig.showTooltips}
                          onChange={(e) => handleInputChange('showTooltips', e.target.checked)}
                          className="checkbox-input"
                          id="show-tooltips"
                        />
                      </td>
                      <td className="label-cell">
                        <label htmlFor="show-tooltips" className="option-label">Show tooltips on hover</label>
                      </td>
                      <td className="color-cell"></td>
                    </tr>
                    <tr className="option-row">
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={chartConfig.chartType === 'donut' ? chartConfig.showValues : chartConfig.showLabels}
                          onChange={(e) => handleInputChange(chartConfig.chartType === 'donut' ? 'showValues' : 'showLabels', e.target.checked)}
                          className="checkbox-input"
                          id={chartConfig.chartType === 'donut' ? 'show-values' : 'show-labels'}
                        />
                      </td>
                      <td className="label-cell">
                        <label htmlFor={chartConfig.chartType === 'donut' ? 'show-values' : 'show-labels'} className="option-label">
                          {chartConfig.chartType === 'donut' ? 'Show Values' : 'Show data labels'}
                        </label>
                      </td>
                      <td className="color-cell">
                        {(chartConfig.chartType === 'donut' ? chartConfig.showValues : chartConfig.showLabels) && (
                          <input
                            type="color"
                            value={chartConfig.labelColor}
                            onChange={(e) => handleInputChange('labelColor', e.target.value)}
                            className="color-picker"
                            title={chartConfig.chartType === 'donut' ? 'Choose color for values' : 'Choose color for data labels'}
                          />
                        )}
                      </td>
                    </tr>
                    {/* Show Percentage - Donut & Funnel */}
                    {(chartConfig.chartType === 'donut' ||
                      chartConfig.chartType === 'funnel') && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={chartConfig.showPercentages}
                            onChange={(e) => handleInputChange('showPercentages', e.target.checked)}
                            className="checkbox-input"
                            id="show-percentages"
                          />
                        </td>
                        <td className="label-cell">
                          <label htmlFor="show-percentages" className="option-label">Show Percentage</label>
                        </td>
                        <td className="color-cell">
                          {chartConfig.showPercentages && (
                            <input
                              type="color"
                              value={chartConfig.labelColor}
                              onChange={(e) => handleInputChange('labelColor', e.target.value)}
                              className="color-picker"
                              title="Choose color for percentages"
                            />
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Full height chart area (100% of container) */}
                    {chartConfig.chartType !== 'donut' &&
                      chartConfig.chartType !== 'funnel' &&
                      chartConfig.chartType !== 'scatter' && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={!!chartConfig.chartFullHeight}
                            onChange={(e) =>
                              handleInputChange('chartFullHeight', e.target.checked)
                            }
                            className="checkbox-input"
                            id="chart-full-height"
                          />
                        </td>
                        <td className="label-cell">
                          <label htmlFor="chart-full-height" className="option-label">
                            Use full height (100%)
                          </label>
                        </td>
                        <td className="color-cell"></td>
                      </tr>
                    )}

                    {/* Stacked / matrix: normalize each bar to 100% */}
                    {(isMatrixReport ||
                      chartConfig.chartType === 'stackedColumn' ||
                      chartConfig.chartType === 'stackedBar') && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={!!chartConfig.stackTo100Percent}
                            onChange={(e) =>
                              handleInputChange('stackTo100Percent', e.target.checked)
                            }
                            className="checkbox-input"
                            id="stack-to-100-percent"
                          />
                        </td>
                        <td className="label-cell">
                          <label htmlFor="stack-to-100-percent" className="option-label">
                            Stack to 100%
                          </label>
                        </td>
                        <td className="color-cell"></td>
                      </tr>
                    )}

                    {/* Matrix: optional Total column in chart (tooltip total always shown) */}
                    {isMatrixReport &&
                      chartConfig.chartType !== 'donut' &&
                      chartConfig.chartType !== 'funnel' &&
                      chartConfig.chartType !== 'scatter' && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={!!chartConfig.showMatrixColumnTotal}
                            disabled={!!chartConfig.stackTo100Percent}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setChartConfig((prev) => {
                                const next = {
                                  ...prev,
                                  showMatrixColumnTotal: checked,
                                };
                                if (
                                  checked &&
                                  !prev.fieldColors?.[MATRIX_CHART_TOTAL_COLOR_KEY] &&
                                  !prev.legendColors?.[MATRIX_CHART_TOTAL_COLOR_KEY]
                                ) {
                                  next.fieldColors = {
                                    ...prev.fieldColors,
                                    [MATRIX_CHART_TOTAL_COLOR_KEY]:
                                      MATRIX_CHART_TOTAL_DEFAULT_COLOR,
                                  };
                                }
                                return next;
                              });
                            }}
                            className="checkbox-input"
                            id="show-matrix-column-total"
                            title={
                              chartConfig.stackTo100Percent
                                ? 'Not available when Stack to 100% is enabled (tooltip still shows Total)'
                                : undefined
                            }
                          />
                        </td>
                        <td className="label-cell">
                          <label
                            htmlFor="show-matrix-column-total"
                            className={`option-label${
                              chartConfig.stackTo100Percent ? ' text-muted' : ''
                            }`}
                            title={
                              chartConfig.stackTo100Percent
                                ? 'Turn off Stack to 100% to show a Total column in the chart'
                                : undefined
                            }
                          >
                            Show Total column in chart
                          </label>
                        </td>
                        <td className="color-cell">
                          {chartConfig.showMatrixColumnTotal &&
                            !chartConfig.stackTo100Percent && (
                            <input
                              type="color"
                              value={getMatrixTotalColor()}
                              onChange={(e) =>
                                handleFieldColorChange(
                                  MATRIX_CHART_TOTAL_COLOR_KEY,
                                  e.target.value
                                )
                              }
                              className="color-picker"
                              title="Choose color for Total column"
                            />
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Show Total - Donut & Funnel (sum of slice values = report Grand Total) */}
                    {(chartConfig.chartType === 'donut' ||
                      chartConfig.chartType === 'funnel') && (
                      <tr className="option-row">
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={chartConfig.showTotal}
                            onChange={(e) => handleInputChange('showTotal', e.target.checked)}
                            className="checkbox-input"
                            id="show-total"
                          />
                        </td>
                        <td className="label-cell">
                          <label htmlFor="show-total" className="option-label">Show Total</label>
                        </td>
                        <td className="color-cell"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <small className="form-help">
                Control how data is displayed on the chart. Tooltips show values on hover
                {isMatrixReport
                  ? ' (including a Total line for each category).'
                  : chartConfig.chartType === 'donut'
                    ? ', values show directly on donut segments'
                    : chartConfig.chartType === 'funnel'
                      ? ', labels show stage counts; Show Total displays the grand total'
                      : ', labels show values directly on chart elements'}
                .
              </small>
            </div>

            {/* Legend Colors Section - Only for Donut Charts */}
            {chartConfig.chartType === 'donut' && summarizedData && Object.keys(summarizedData).length > 0 && (
              <div className="form-group">
                <label className="form-label">Legend Colors</label>
                <div className="chart-display-options">
                  <div className="options-container">
                    <table className="options-table legend-colors-table">
                      <tbody>
                        {(() => {
                          // Get unique labels from summarizedData
                          const labels = new Set();
                          const sliceField = chartConfig.pieSliceField;

                          if (sliceField && summarizedData[sliceField]) {
                            Object.keys(summarizedData[sliceField]).forEach(groupValue => {
                              labels.add(groupValue);
                            });
                          }

                          const labelsArray = Array.from(labels);
                          return labelsArray.map((label, index) => {
                            const defaultRgbaColor = getDefaultColorForLabel(index);
                            const defaultHexColor = getDefaultHexColorForLabel(index);
                            const currentColor = chartConfig.legendColors[label] || defaultHexColor;
                            const isCustomColor = chartConfig.legendColors[label];

                            return (
                              <tr key={label} className="option-row">
                                <td className="number-cell">
                                  <span className="legend-number">{index + 1}</span>
                                </td>
                                <td className="label-cell">
                                  <label className="option-label">{label}</label>
                                </td>
                                <td className="color-cell">
                                  <div className="color-controls">
                                    <input
                                      type="color"
                                      value={currentColor}
                                      onChange={(e) => handleLegendColorChange(label, e.target.value)}
                                      className="color-picker"
                                      title={`Choose color for ${label}`}
                                    />
                                    {isCustomColor && (
                                      <button
                                        type="button"
                                        onClick={() => resetLegendColorToDefault(label)}
                                        className="reset-color-btn"
                                        title={`Reset to default color`}
                                      >
                                        ↺
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <small className="form-help">Customize colors for each legend item. Changes will be reflected in the chart immediately.</small>
                </div>
              </div>
            )}
          </div>

          <div className="chart-preview-section">
            <h4>Chart Preview</h4>

            {/* Record Count Information */}


            <div className="chart-preview-container">
              {isLoading ? (
                <div className="chart-loading">
                  <div className="loading-spinner"></div>
                  <p>Generating chart...</p>
                </div>
              ) : (
                <div className="chart-preview">
                  {renderChart()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="chart-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveChart}
            disabled={!chartConfig.title.trim() || isSaving}
          >
            {isSaving
              ? 'Saving...'
              : editingChart
                ? 'Update Chart'
                : 'Add Chart'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportChart;
